import classnames from 'classnames';
import * as React from 'react';

const icomoonStyle = require('../common/icon-font-icomoon/style.css');
const style = require('../common/icon-font-icomoon/futurefit-icons-common.css');

export type IconSizes = 'xxsmall' | 'xsmall' | 'small' | 'small-medium' | 'medium' | 'large' | 'xlarge';

export type IconColors = 'black' | 'violet' | 'red' | 'green' | 'blue' | 'orange' | 'white' | 'primary' | 'secondary';

export interface IconProps {
  /** Additional classes */
  className?: string;
  /** Color (Default: black) */
  color?: IconColors;
  /** Size otherwise medium */
  size?: IconSizes;
  /** Callback on click */
  onClick?(): void;
  /** Callback on Mouse Enter */
  onMouseEnter?(): void;
  /** Callback on Mouse Exit */
  onMouseLeave?(): void;
}

/** All icons for easy iterating */
export const ALL_ICONS: {
  name: string;
  Icon(props: IconProps): JSX.Element;
}[] = [];

/** Experimental */
function makeSVGIcon(name: string, svg: JSX.Element): (props: IconProps) => JSX.Element {
  const Icon = (props: IconProps) => {
    const size = props.size || 'medium';
    return (
      <div
        className={classnames(
          style.svg,
          style.icon,
          style[size],
          props.color && style[props.color],
          props.onClick && style.clickable,
          props.className
        )}
        onClick={props.onClick}
        onMouseEnter={props.onMouseEnter}
        onMouseLeave={props.onMouseLeave}
      >
        {svg}
      </div>
    );
  };
  ALL_ICONS.push({
    name,
    Icon
  });
  return Icon;
}

function makeIcon(name: string): (props: IconProps) => JSX.Element {
  const Icon = (props: IconProps) => {
    const size = props.size || 'medium';
    return (
      <span
        className={classnames(
          icomoonStyle[`futurefit-icons-${name}`],
          style.icon,
          style[size],
          props.color && style[props.color],
          props.onClick && style.clickable,
          props.className
        )}
        onClick={props.onClick}
        onMouseEnter={props.onMouseEnter}
        onMouseLeave={props.onMouseLeave}
      />
    );
  };
  ALL_ICONS.push({
    name,
    Icon
  });
  return Icon;
}

export const AccordionClosedIcon = makeIcon('accordion-closed');
export const AccordionOpenIcon = makeIcon('accordion-open');
export const AddPlaceIcon = makeIcon('add-place');
export const AlcoholIcon = makeIcon('alcohol');
export const AppleIcon = makeIcon('apple');
export const ArrowDownIcon = makeIcon('arrow-down');
export const ArrowLeftIcon = makeIcon('arrow-left');
export const ArrowRightIcon = makeIcon('arrow-right');
export const ArrowUpIcon = makeIcon('arrow-up');
export const BackIcon = makeIcon('back');
export const BicycleIcon = makeIcon('bicycle');
export const BusinessesIcon = makeIcon('businesses');
export const CalendarIcon = makeIcon('calendar');
export const CheckboxMinusIcon = makeIcon('checkbox-minus-fill');
export const CheckboxOffIcon = makeIcon('checkbox-off');
export const CheckboxOnIcon = makeIcon('checkbox-on');
export const CheckedIcon = makeIcon('checked');
export const CloseIcon = makeIcon('close');
export const CommentIcon = makeIcon('comment');
export const ConfirmationIcon = makeIcon('confirmation');
export const DeskIcon = makeIcon('desk');
export const DiabetesIcon = makeIcon('diabetes');
export const DocumentIcon = makeIcon('document');
export const EditIcon = makeIcon('edit');
export const EmailIcon = makeIcon('email');
export const ErrorIcon = makeIcon('error');
export const FaceToFaceIcon = makeIcon('face-to-face');
export const FacebookIcon = makeIcon('facebook');
export const FastFoodIcon = makeIcon('fast-food');
export const FruitAndVegIcon = makeIcon('fruit-and-veg');
export const FruitsIcon = makeIcon('fruits');
export const HLCIcon = makeIcon('hlc');
export const HeartBeatIcon = makeIcon('heart-beat');
export const HeartDiseaseIcon = makeIcon('heart-disease');
export const HeartIcon = makeIcon('heart');
export const HeartOutlineIcon = makeIcon('heart-outline');
export const HelpIcon = makeIcon('help');
export const HomeIcon = makeIcon('home');
export const HourglassIcon = makeIcon('hourglass');
export const InfoIcon = makeIcon('info');
export const InstagramIcon = makeIcon('instagram');
export const LinkedinIcon = makeIcon('linkedin');
export const LockIcon = makeIcon('lock');
export const LogoutIcon = makeIcon('logout');
export const MailSentIcon = makeIcon('mail-sent');
export const MembersIcon = makeIcon('members');
export const MentalHealthIcon = makeIcon('mental-health');
export const MenuIcon = makeIcon('menu');
export const NextIcon = makeIcon('next');
export const OnlineIcon = makeIcon('online');
export const PeopleIcon = makeIcon('people');
export const PhoneIcon = makeIcon('phone');
export const PhysicalActivityIcon = makeIcon('physical-activity');
export const PlusIcon = makeIcon('plus');
export const PosterIcon = makeIcon('poster');
export const PresentationIcon = makeIcon('presentation');
export const PrinterIcon = makeIcon('printer');
export const QuitSmokingIcon = makeIcon('quit-smoking');
export const RadioChecked = makeIcon('radio-checked');
export const RadioChecked2 = makeIcon('radio-checked2');
export const RadioUnchecked = makeIcon('radio-unchecked');
export const ServiceProvidersIcon = makeIcon('service-providers');
export const SettingsIcon = makeIcon('settings');
export const SmokingIcon = makeIcon('smoking');
export const StatsIcon = makeIcon('stats');
export const SugaryDrinkIcon = makeIcon('sugary-drink');
export const TemplateIcon = makeIcon('template');
export const TickCircleIcon = makeIcon('tick-circle');
export const TimeIcon = makeIcon('time');
export const TvIcon = makeIcon('tv');
export const TwitterIcon = makeIcon('twitter');
export const UserIcon = makeIcon('user');
export const VegetableIcon = makeIcon('vegetable');
export const VideoIcon = makeIcon('video');
export const WarningIcon = makeIcon('warning');
export const WaterIntakeIcon = makeIcon('water-intake');
export const WebsiteIcon = makeIcon('website');
export const WorkersSurveyIcon = makeIcon('workers-survey');
export const WorkplaceIcon = makeIcon('workplace');
export const YoutubeIcon = makeIcon('youtube');
// export const BinIcon = makeIcon("bin");

const FilterSVG: JSX.Element = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23.95 23.14">
    <path
      d="M23.2,1.55a.75.75,0,0,0-.75-.8H1.5a.75.75,0,0,0-.75.8,11.26,11.26,0,0,0,9,10.22v9.88a.73.73,0,0,0,.44.67.72.72,0,0,0,.79-.1l3-2.5a.74.74,0,0,0,.27-.57V11.77A11.26,11.26,0,0,0,23.2,1.55Z"
      style={{
        fill: 'none',
        stroke: '#312f30',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeWidth: '1.5px'
      }}
    />
  </svg>
);

const BinSVG: JSX.Element = (
  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23.95 23.14">
    <path
      d="M128 640v-640c0-35.2 28.8-64 64-64h576c35.2 0 64 28.8 64 64v640h-704zM320 64h-64v448h64v-448zM448 64h-64v448h64v-448zM576 64h-64v448h64v-448zM704 64h-64v448h64v-448zM848 832h-208v80c0 26.4-21.6 48-48 48h-224c-26.4 0-48-21.6-48-48v-80h-208c-26.4 0-48-21.6-48-48v-80h832v80c0 26.4-21.6 48-48 48zM576 832h-192v63.198h192v-63.198z"
      style={{
        fill: 'none',
        stroke: '#312f30',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        strokeWidth: '1.5px'
      }}
    />
  </svg>
);

export const FilterSVGIcon = makeSVGIcon('filter', FilterSVG);
export const BinSVGIcon = makeSVGIcon('bin', BinSVG);



// WEBPACK FOOTER //
// ./src/ui/common/icons.tsx